import { RoutesPaths } from "../../../constants/routes.ts"
import { type OnboardingSignals } from "../hooks/use-onboarding-signals.ts"
import { isProductTourStepPermitted } from "./product-tour-filter.ts"

/**
 * The workspace setup ladder: Personal → Company → Team → Permissions →
 * Notifications → Templates, then an optional Communication step for whoever
 * manages the company's customer email and texting. This is the single source both
 * the Guide Home stepper/current-step card and the Setup Assistant panel
 * read — a step completed on either surface is complete everywhere.
 *
 * "Personal" and "Notifications" have no `requiresPage` — every user
 * configures their own profile and their own notification preferences.
 * Everything else is company-level configuration, gated on the exact same
 * dot-path code its real Settings page itself is gated on (never a second,
 * invented permission model) — "Your First Workflow" (creating a first
 * Lead) is deliberately not part of this ladder — it's product adoption,
 * not workspace setup, and belongs to a separate, not-yet-built moment.
 */
export type OnboardingItemId =
  | "personal"
  | "company"
  | "team"
  | "permissions"
  | "notifications"
  | "templates"
  | "communication"

/**
 * Three different completion semantics — not every setup step can be
 * objectively "done":
 * - COMPLETE: real, validatable application data (Personal, Company, Team).
 * - REVIEW: no objectively correct configuration to check against — the
 *   goal is that the user looked at it, not that it's "correct" (Permissions,
 *   Notifications). Recorded via GuideProgress.reviewedStepIds.
 * - OPTIONAL: useful but not required, and a real signal may exist for it,
 *   but skipping it must never block setup completion (Templates). Recorded
 *   via GuideProgress.skippedStepIds.
 */
export type OnboardingStepType = "complete" | "review" | "optional"

/** Only the fields SettingsProfileForm's own validation actually requires
 * (PersonalInformationFormSection.tsx: first_name/last_name/email/phone are
 * all `required: true`) — this is the real definition of "done," not a
 * guess, and it's read straight off state.auth.userInfo with no extra call. */
export interface PersonalInfoLike {
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  phone?: string | null
}

export const isPersonalInfoComplete = (
  userInfo: PersonalInfoLike | null | undefined
): boolean =>
  !!(
    userInfo?.first_name &&
    userInfo?.last_name &&
    userInfo?.email &&
    userInfo?.phone
  )

/** Only the fields CompanyInformationFormSection's own validation actually
 * requires (business_name/company_phone/license_number are all
 * `required: true` — address, tax id, bond/insurance numbers, and the logo,
 * are not). The logo specifically has no real completion signal at all
 * (CompanyInformationFormSection never marks it required) — the Setup
 * Assistant's "Company logo" row is guide-checklist-only for exactly that
 * reason, never folded into this. */
export interface CompanyInfoLike {
  business_name?: string | null
  company_phone?: string | null
  license_number?: string | null
}

export const isCompanyInfoComplete = (
  company: CompanyInfoLike | null | undefined
): boolean =>
  !!(
    company?.business_name &&
    company?.company_phone &&
    company?.license_number
  )

/** One Setup Assistant checklist row. Every row is independently toggleable
 * (a personal "I reviewed/did this" mark, `GuideProgress.checkedGuideItemIds`
 * — never real completion data); a row that also names a `target` OR a
 * `route` gets a second, separate "View →" control — `target` scrolls to
 * and highlights a real `data-guide-target` section on the parent item's
 * own page (Personal, Company), `route` navigates to a different real page
 * entirely (Templates, whose four areas each live on their own screen). The
 * two are mutually exclusive; the checkbox and View are always independent
 * actions, so clicking one never fights clicking the other. */
export interface OnboardingSubStep {
  id: string
  labelKey: string
  descriptionKey?: string
  target?: string
  route?: string
}

export interface OnboardingItem {
  id: OnboardingItemId
  stepType: OnboardingStepType
  labelKey: string
  /** A shorter label for the compact horizontal stepper. */
  shortLabelKey: string
  descriptionKey: string
  /** A short "why EZBuild needs this" callout — omitted where the
   * description already says enough on its own. */
  helperCalloutKey?: string
  ctaKey: string
  linkTo: string
  durationMinutes: number
  /** Derived automatically from useOnboardingSignals; omitted + not
   * autoDetected means the step has no cheap real-data check of its own
   * (REVIEW steps, or an OPTIONAL step with nothing to count yet). */
  signal?: keyof OnboardingSignals
  /** True for a completion condition derived some other way from real
   * product state (currently just "personal", from the user's own
   * profile) — like `signal`, never a review/skip flag. */
  autoDetected?: boolean
  /** Omitted = shown to everyone. A bare string is a `permissions.pages`
   * entry; a `category.section.key` dot-path is resolved against
   * `permissions.codes` — the exact shape `PermissionGate` itself reads,
   * matching this step's own real Settings-page gate exactly (never a
   * coarser or invented check). An array is an OR of either form, for a
   * step (Templates) whose real destination is itself gated on "any of". */
  requiresPage?: string | string[]
  subSteps?: OnboardingSubStep[]
}

export const ONBOARDING_ITEMS: OnboardingItem[] = [
  {
    id: "personal",
    stepType: "complete",
    labelKey: "guide.onboarding.items.personal.label",
    shortLabelKey: "guide.onboarding.items.personal.shortLabel",
    descriptionKey: "guide.onboarding.items.personal.description",
    ctaKey: "guide.onboarding.items.personal.cta",
    linkTo: RoutesPaths.settings.profile,
    durationMinutes: 2,
    autoDetected: true,
    subSteps: [
      {
        id: "basicInfo",
        labelKey: "guide.setupAssistant.personal.checklist.basicInfo.label",
        descriptionKey:
          "guide.setupAssistant.personal.checklist.basicInfo.description",
        target: "personal-basic-info"
      },
      {
        id: "contactInfo",
        labelKey: "guide.setupAssistant.personal.checklist.contactInfo.label",
        descriptionKey:
          "guide.setupAssistant.personal.checklist.contactInfo.description",
        target: "personal-contact-info"
      },
      {
        id: "photo",
        labelKey: "guide.setupAssistant.personal.checklist.photo.label",
        descriptionKey:
          "guide.setupAssistant.personal.checklist.photo.description",
        target: "personal-photo-section"
      }
    ]
  },
  {
    id: "company",
    stepType: "complete",
    labelKey: "guide.onboarding.items.company.label",
    shortLabelKey: "guide.onboarding.items.company.shortLabel",
    descriptionKey: "guide.onboarding.items.company.description",
    helperCalloutKey: "guide.setupAssistant.company.helperCallout",
    ctaKey: "guide.onboarding.items.company.cta",
    linkTo: RoutesPaths.settings.company,
    durationMinutes: 3,
    signal: "hasCompanyInfo",
    requiresPage: "settings.company.settings_company_view",
    subSteps: [
      {
        id: "companyDetails",
        labelKey: "guide.setupAssistant.company.checklist.companyDetails.label",
        descriptionKey:
          "guide.setupAssistant.company.checklist.companyDetails.description",
        target: "company-details-section"
      },
      {
        id: "licenseInformation",
        labelKey:
          "guide.setupAssistant.company.checklist.licenseInformation.label",
        descriptionKey:
          "guide.setupAssistant.company.checklist.licenseInformation.description",
        target: "company-license-section"
      },
      {
        id: "logo",
        labelKey: "guide.setupAssistant.company.checklist.logo.label",
        descriptionKey:
          "guide.setupAssistant.company.checklist.logo.description",
        target: "company-logo-section"
      }
    ]
  },
  {
    id: "team",
    stepType: "complete",
    labelKey: "guide.onboarding.items.team.label",
    shortLabelKey: "guide.onboarding.items.team.shortLabel",
    descriptionKey: "guide.onboarding.items.team.description",
    helperCalloutKey: "guide.setupAssistant.team.helperCallout",
    ctaKey: "guide.onboarding.items.team.cta",
    linkTo: RoutesPaths.team_management,
    durationMinutes: 3,
    signal: "hasTeammates",
    requiresPage: "team_management",
    subSteps: [
      {
        id: "addTeamMembers",
        labelKey: "guide.setupAssistant.team.checklist.addTeamMembers.label",
        descriptionKey:
          "guide.setupAssistant.team.checklist.addTeamMembers.description"
      },
      {
        id: "assignRoles",
        labelKey: "guide.setupAssistant.team.checklist.assignRoles.label",
        descriptionKey:
          "guide.setupAssistant.team.checklist.assignRoles.description"
      },
      {
        id: "reviewTeamAccess",
        labelKey: "guide.setupAssistant.team.checklist.reviewTeamAccess.label",
        descriptionKey:
          "guide.setupAssistant.team.checklist.reviewTeamAccess.description"
      }
    ]
  },
  {
    id: "permissions",
    stepType: "review",
    labelKey: "guide.onboarding.items.permissions.label",
    shortLabelKey: "guide.onboarding.items.permissions.shortLabel",
    descriptionKey: "guide.onboarding.items.permissions.description",
    ctaKey: "guide.onboarding.items.permissions.cta",
    linkTo: RoutesPaths.settings.permissions,
    durationMinutes: 3,
    requiresPage: "settings.permissions.settings_permissions_view",
    subSteps: [
      {
        id: "rolePermissions",
        labelKey:
          "guide.setupAssistant.permissions.checklist.rolePermissions.label",
        descriptionKey:
          "guide.setupAssistant.permissions.checklist.rolePermissions.description"
      },
      {
        id: "sensitiveAccess",
        labelKey:
          "guide.setupAssistant.permissions.checklist.sensitiveAccess.label",
        descriptionKey:
          "guide.setupAssistant.permissions.checklist.sensitiveAccess.description"
      },
      {
        id: "confirmPermissions",
        labelKey:
          "guide.setupAssistant.permissions.checklist.confirmPermissions.label",
        descriptionKey:
          "guide.setupAssistant.permissions.checklist.confirmPermissions.description"
      }
    ]
  },
  {
    id: "notifications",
    stepType: "review",
    labelKey: "guide.onboarding.items.notifications.label",
    shortLabelKey: "guide.onboarding.items.notifications.shortLabel",
    descriptionKey: "guide.onboarding.items.notifications.description",
    ctaKey: "guide.onboarding.items.notifications.cta",
    linkTo: RoutesPaths.settings.notifications,
    durationMinutes: 2,
    subSteps: [
      {
        id: "inApp",
        labelKey: "guide.setupAssistant.notifications.checklist.inApp.label",
        descriptionKey:
          "guide.setupAssistant.notifications.checklist.inApp.description"
      },
      {
        id: "email",
        labelKey: "guide.setupAssistant.notifications.checklist.email.label",
        descriptionKey:
          "guide.setupAssistant.notifications.checklist.email.description"
      },
      {
        id: "pushAndDesktop",
        labelKey:
          "guide.setupAssistant.notifications.checklist.pushAndDesktop.label",
        descriptionKey:
          "guide.setupAssistant.notifications.checklist.pushAndDesktop.description"
      }
    ]
  },
  {
    id: "templates",
    stepType: "optional",
    labelKey: "guide.onboarding.items.templates.label",
    shortLabelKey: "guide.onboarding.items.templates.shortLabel",
    descriptionKey: "guide.onboarding.items.templates.description",
    ctaKey: "guide.onboarding.items.templates.cta",
    linkTo: RoutesPaths.settings.templates,
    durationMinutes: 4,
    signal: "hasProjectTypes",
    // The real Templates hub (SettingsTemplatesHub) itself renders behind
    // "any of" these three codes — matched here exactly rather than the
    // coarser team_management page, so a user who can reach the hub is
    // never told this step doesn't apply to them, and vice versa.
    requiresPage: [
      "settings.project_templates.settings_template_view",
      "settings.document_templates.settings_proposal_template_view",
      "settings.permissions.settings_permissions_view"
    ],
    // Ids deliberately match `TemplateCountKey` (use-template-counts.ts) —
    // each row's real count comes straight from the same hook/data
    // `SettingsTemplatesHub` itself reads, keyed the same way, so there is
    // no second mapping table to keep in sync.
    subSteps: [
      {
        id: "project",
        labelKey: "guide.setupAssistant.templates.checklist.project.label",
        descriptionKey:
          "guide.setupAssistant.templates.checklist.project.description",
        route: RoutesPaths.settings.projectTemplates
      },
      {
        id: "documents",
        labelKey: "guide.setupAssistant.templates.checklist.documents.label",
        descriptionKey:
          "guide.setupAssistant.templates.checklist.documents.description",
        route: RoutesPaths.settings.documentTemplates
      },
      {
        id: "dashboard",
        labelKey: "guide.setupAssistant.templates.checklist.dashboard.label",
        descriptionKey:
          "guide.setupAssistant.templates.checklist.dashboard.description",
        route: RoutesPaths.settings.dashboardTemplates
      },
      {
        id: "lists",
        labelKey: "guide.setupAssistant.templates.checklist.lists.label",
        descriptionKey:
          "guide.setupAssistant.templates.checklist.lists.description",
        route: RoutesPaths.settings.lists
      }
    ]
  },
  {
    id: "communication",
    stepType: "review",
    labelKey: "guide.onboarding.items.communication.label",
    shortLabelKey: "guide.onboarding.items.communication.shortLabel",
    descriptionKey: "guide.onboarding.items.communication.description",
    ctaKey: "guide.onboarding.items.communication.cta",
    linkTo: `${RoutesPaths.communication.overview}?settings=email`,
    durationMinutes: 3,
    requiresPage: "communication.messages.communication_settings_manage",
    subSteps: [
      {
        id: "identity",
        labelKey: "guide.setupAssistant.communication.checklist.identity.label",
        descriptionKey:
          "guide.setupAssistant.communication.checklist.identity.description"
      },
      {
        id: "domain",
        labelKey: "guide.setupAssistant.communication.checklist.domain.label",
        descriptionKey:
          "guide.setupAssistant.communication.checklist.domain.description"
      },
      {
        id: "sms",
        labelKey: "guide.setupAssistant.communication.checklist.sms.label",
        descriptionKey:
          "guide.setupAssistant.communication.checklist.sms.description"
      }
    ]
  }
]

/** The four areas behind Templates' own checklist rows — deliberately the
 * same ids as those rows' own (and `TemplateCountKey`'s) so a subsection is
 * addressed the exact same way its row is, no second id scheme. */
export type TemplateSubsectionId =
  | "project"
  | "documents"
  | "dashboard"
  | "lists"

export interface TemplateSubsectionChecklistItem {
  id: string
  labelKey: string
}

/** The Setup Assistant's contextual guide for one Templates area, shown in
 * the panel once its row's "View →" is clicked — real navigation to the
 * area's own real page, with the panel switching from the main Templates
 * checklist into this instead of closing. Its own `checklist` is guide-only
 * (Level A, "I reviewed this"), same as every other step's — visiting an
 * area is guidance, never a requirement to create something before "Back to
 * Templates" is allowed. */
export interface TemplateSubsectionGuide {
  id: TemplateSubsectionId
  titleKey: string
  bodyKey: string
  /** A short "such as ..." bullet list grounding the area in real
   * construction-business examples — never a real, fetched list. Omitted
   * (empty array) where the area needs no examples to make sense. */
  exampleLabelKey: string
  exampleKeys: string[]
  /** A second explanatory line — e.g. how the area connects to the rest of
   * EZBuild — shown after the examples where one is present. */
  helperKey?: string
  checklistTitleKey: string
  checklist: TemplateSubsectionChecklistItem[]
  ctaKey: string
  route: string
}

export const TEMPLATE_SUBSECTION_GUIDES: Record<
  TemplateSubsectionId,
  TemplateSubsectionGuide
> = {
  project: {
    id: "project",
    titleKey: "guide.setupAssistant.templates.subsections.project.title",
    bodyKey: "guide.setupAssistant.templates.subsections.project.body",
    exampleLabelKey:
      "guide.setupAssistant.templates.subsections.project.exampleLabel",
    exampleKeys: [
      "guide.setupAssistant.templates.subsections.project.examples.kitchen",
      "guide.setupAssistant.templates.subsections.project.examples.bathroom",
      "guide.setupAssistant.templates.subsections.project.examples.adu",
      "guide.setupAssistant.templates.subsections.project.examples.roofing",
      "guide.setupAssistant.templates.subsections.project.examples.newConstruction"
    ],
    checklistTitleKey:
      "guide.setupAssistant.templates.subsections.project.checklistTitle",
    checklist: [
      {
        id: "createOrReview",
        labelKey:
          "guide.setupAssistant.templates.subsections.project.checklist.createOrReview"
      },
      {
        id: "addStructure",
        labelKey:
          "guide.setupAssistant.templates.subsections.project.checklist.addStructure"
      },
      {
        id: "addMilestones",
        labelKey:
          "guide.setupAssistant.templates.subsections.project.checklist.addMilestones"
      },
      {
        id: "reviewWorkflow",
        labelKey:
          "guide.setupAssistant.templates.subsections.project.checklist.reviewWorkflow"
      }
    ],
    ctaKey: "guide.setupAssistant.templates.subsections.project.cta",
    route: RoutesPaths.settings.projectTemplates
  },
  documents: {
    id: "documents",
    titleKey: "guide.setupAssistant.templates.subsections.documents.title",
    bodyKey: "guide.setupAssistant.templates.subsections.documents.body",
    exampleLabelKey:
      "guide.setupAssistant.templates.subsections.documents.exampleLabel",
    exampleKeys: [],
    helperKey: "guide.setupAssistant.templates.subsections.documents.helper",
    checklistTitleKey:
      "guide.setupAssistant.templates.subsections.documents.checklistTitle",
    checklist: [
      {
        id: "reviewProposals",
        labelKey:
          "guide.setupAssistant.templates.subsections.documents.checklist.reviewProposals"
      },
      {
        id: "reviewContracts",
        labelKey:
          "guide.setupAssistant.templates.subsections.documents.checklist.reviewContracts"
      },
      {
        id: "createProposal",
        labelKey:
          "guide.setupAssistant.templates.subsections.documents.checklist.createProposal"
      },
      {
        id: "createContract",
        labelKey:
          "guide.setupAssistant.templates.subsections.documents.checklist.createContract"
      }
    ],
    ctaKey: "guide.setupAssistant.templates.subsections.documents.cta",
    route: RoutesPaths.settings.documentTemplates
  },
  dashboard: {
    id: "dashboard",
    titleKey: "guide.setupAssistant.templates.subsections.dashboard.title",
    bodyKey: "guide.setupAssistant.templates.subsections.dashboard.body",
    exampleLabelKey:
      "guide.setupAssistant.templates.subsections.dashboard.exampleLabel",
    exampleKeys: [
      "guide.setupAssistant.templates.subsections.dashboard.examples.projectManager",
      "guide.setupAssistant.templates.subsections.dashboard.examples.sales",
      "guide.setupAssistant.templates.subsections.dashboard.examples.owner",
      "guide.setupAssistant.templates.subsections.dashboard.examples.finance"
    ],
    helperKey: "guide.setupAssistant.templates.subsections.dashboard.helper",
    checklistTitleKey:
      "guide.setupAssistant.templates.subsections.dashboard.checklistTitle",
    checklist: [
      {
        id: "reviewExisting",
        labelKey:
          "guide.setupAssistant.templates.subsections.dashboard.checklist.reviewExisting"
      },
      {
        id: "createTemplate",
        labelKey:
          "guide.setupAssistant.templates.subsections.dashboard.checklist.createTemplate"
      },
      {
        id: "chooseWidgets",
        labelKey:
          "guide.setupAssistant.templates.subsections.dashboard.checklist.chooseWidgets"
      },
      {
        id: "arrangeLayout",
        labelKey:
          "guide.setupAssistant.templates.subsections.dashboard.checklist.arrangeLayout"
      }
    ],
    ctaKey: "guide.setupAssistant.templates.subsections.dashboard.cta",
    route: RoutesPaths.settings.dashboardTemplates
  },
  lists: {
    id: "lists",
    titleKey: "guide.setupAssistant.templates.subsections.lists.title",
    bodyKey: "guide.setupAssistant.templates.subsections.lists.body",
    exampleLabelKey:
      "guide.setupAssistant.templates.subsections.lists.exampleLabel",
    exampleKeys: [
      "guide.setupAssistant.templates.subsections.lists.examples.leadSources",
      "guide.setupAssistant.templates.subsections.lists.examples.projectTypes",
      "guide.setupAssistant.templates.subsections.lists.examples.documentTypes",
      "guide.setupAssistant.templates.subsections.lists.examples.statuses",
      "guide.setupAssistant.templates.subsections.lists.examples.categories"
    ],
    checklistTitleKey:
      "guide.setupAssistant.templates.subsections.lists.checklistTitle",
    checklist: [
      {
        id: "reviewValues",
        labelKey:
          "guide.setupAssistant.templates.subsections.lists.checklist.reviewValues"
      },
      {
        id: "addValues",
        labelKey:
          "guide.setupAssistant.templates.subsections.lists.checklist.addValues"
      },
      {
        id: "removeValues",
        labelKey:
          "guide.setupAssistant.templates.subsections.lists.checklist.removeValues"
      },
      {
        id: "organizeValues",
        labelKey:
          "guide.setupAssistant.templates.subsections.lists.checklist.organizeValues"
      }
    ],
    ctaKey: "guide.setupAssistant.templates.subsections.lists.cta",
    route: RoutesPaths.settings.lists
  }
}

/** A bare page string, a dot-path, or an array of either (OR'd) — the same
 * resolver `ProductTourStep.requiredPage` uses against `permissions.pages`/
 * `permissions.codes`, so a step's gate is never a second permission model. */
const isRequirementMet = (
  requirement: string | string[] | undefined,
  pages: readonly string[] | null | undefined,
  codes: unknown
): boolean => {
  if (!requirement) return true
  const list = Array.isArray(requirement) ? requirement : [requirement]
  return list.some((entry) => isProductTourStepPermitted(entry, pages, codes))
}

export const visibleOnboardingItems = (
  pages: readonly string[] | null | undefined,
  codes: unknown
): OnboardingItem[] =>
  ONBOARDING_ITEMS.filter((item) =>
    isRequirementMet(item.requiresPage, pages, codes)
  )

export interface OnboardingReviewState {
  reviewedStepIds: string[]
  skippedStepIds: string[]
}

export const isOnboardingItemComplete = (
  item: OnboardingItem,
  signals: OnboardingSignals,
  review: OnboardingReviewState,
  personalInfo?: PersonalInfoLike | null
): boolean => {
  if (item.id === "personal") return isPersonalInfoComplete(personalInfo)

  // Skipping a step — any step, any type — records intent to return later;
  // it never counts as completion. Only an explicit "I've reviewed ..."
  // (REVIEW) or real, fetched product data (OPTIONAL/COMPLETE's own signal)
  // does. `skippedStepIds` is tracked purely so the Setup Assistant can
  // later distinguish "never opened" from "opened and deferred" — it has no
  // bearing on the number here.
  if (item.stepType === "review") {
    return review.reviewedStepIds.includes(item.id)
  }

  if (item.stepType === "optional") {
    return !!(item.signal && signals[item.signal])
  }

  // "complete": only real, fetched signal data counts — never a manual flag.
  if (item.signal) {
    const signalValue = signals[item.signal]
    if (signalValue != null) return signalValue
  }
  return false
}

/** The one setup-progress figure every Guide surface reads, so none of them
 * can ever disagree about how far along a user's setup is. */
export const computeSetupProgress = (
  pages: readonly string[] | null | undefined,
  codes: unknown,
  signals: OnboardingSignals,
  review: OnboardingReviewState,
  personalInfo?: PersonalInfoLike | null
): { completed: number; total: number } => {
  const items = visibleOnboardingItems(pages, codes)
  return {
    completed: items.filter((item) =>
      isOnboardingItemComplete(item, signals, review, personalInfo)
    ).length,
    total: items.length
  }
}

/** Whether a step should stop being "the thing Guide Home points at next" —
 * real completion OR an explicit skip. Deliberately looser than
 * `isOnboardingItemComplete`: skipping a step is enough to move the user's
 * attention on to the next one (so Guide Home never feels stuck on a step
 * they said "not now" to), but it must never count toward the completion
 * total — that's what keeps `computeSetupProgress` honest per this step's
 * own real signal, `isOnboardingItemComplete` alone. */
const isOnboardingItemHandled = (
  item: OnboardingItem,
  signals: OnboardingSignals,
  review: OnboardingReviewState,
  personalInfo?: PersonalInfoLike | null
): boolean =>
  isOnboardingItemComplete(item, signals, review, personalInfo) ||
  review.skippedStepIds.includes(item.id)

/** The current step — the first visible item this user hasn't finished or
 * skipped yet, or null once every visible item is one or the other. Drives
 * the stepper's "current" highlight and the one current-step card on Guide
 * Home — never the completion count, which stays on the stricter,
 * skip-blind `isOnboardingItemComplete`. */
export const nextIncompleteOnboardingItem = (
  pages: readonly string[] | null | undefined,
  codes: unknown,
  signals: OnboardingSignals,
  review: OnboardingReviewState,
  personalInfo?: PersonalInfoLike | null
): OnboardingItem | null =>
  visibleOnboardingItems(pages, codes).find(
    (item) => !isOnboardingItemHandled(item, signals, review, personalInfo)
  ) ?? null
